import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useBeforeunload from '../useBeforeunload';
import {
  ANALYZER_ACTION,
  ANALYZER_ENTITY,
  ANALYZER_TIME_DELAY,
  CONTENT_KEY,
} from '../../commons/constants';
import { RootState } from '../../redux/store';
import { getProject } from '../../redux/selectors/project.selector';
import {
  getSelectedGroup,
  getSelectedSubGroup,
} from '../../redux/selectors/locationGroup.selector';
import { getSelectedFocusLocationItem } from '../../redux/selectors/mapLocation.selector';
import { AnalyzerTempTypes } from '../../types';

type UseAnalyzerLocationPropsTypes = {
  initial: boolean;
};

const useAnalyzerLocation = (props: UseAnalyzerLocationPropsTypes): void => {
  const { initial } = props;

  const [tempGroup, setTempGroup] = useState<AnalyzerTempTypes>();
  const [tempSubGroup, setTempSubGroup] = useState<AnalyzerTempTypes>();
  const [tempLocation, setTempLocation] = useState<AnalyzerTempTypes>();

  const contentActiveKey = useSelector((state: RootState) => state.projectModel.contentActiveKey);
  const isActivityWindow = useSelector((state: RootState) => state.layoutModel.isActivityWindow);
  const project = useSelector(getProject);
  const group = useSelector(getSelectedGroup);
  const subGroup = useSelector(getSelectedSubGroup);
  const location = useSelector(getSelectedFocusLocationItem);

  const groupAnalyzer = (action: string, code: string, name: string) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.LOCATION_GROUP, {
        project_code: project.code,
        project_name: project.name,
        locationgroup_code: code,
        locationgroup_name: name,
      });
    }
  };

  const subGroupAnalyzer = (action: string, code: string, name: string) => {
    if (!project || !tempGroup) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.LOCATION_SUBGROUP, {
        project_code: project.code,
        project_name: project.name,
        locationgroup_code: tempGroup.code,
        locationgroup_name: tempGroup.name,
        locationsubgroup_code: code,
        locationsubgroup_name: name,
      });
    }
  };

  const locationAnalyzer = (action: string, code: string, name: string) => {
    if (!project || !tempGroup) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.LOCATION, {
        project_code: project.code,
        project_name: project.name,
        locationgroup_code: tempGroup.code,
        locationgroup_name: tempGroup.name,
        locationsubgroup_code: tempSubGroup ? tempSubGroup.code : null,
        locationsubgroup_name: tempSubGroup ? tempSubGroup.name : null,
        location_code: code,
        location_name: name,
      });
    }
  };

  // run event when close tab window or reload page
  useBeforeunload(async (event) => {
    if (!initial) return;
    const promiseAll = await Promise.all([
      new Promise((resolve) => {
        if (tempGroup) {
          groupAnalyzer(ANALYZER_ACTION.CLOSED, tempGroup.code, tempGroup.name);
        }

        resolve(true);
      }),
      new Promise((resolve) => {
        if (tempSubGroup) {
          subGroupAnalyzer(ANALYZER_ACTION.CLOSED, tempSubGroup.code, tempSubGroup.name);
        }

        resolve(true);
      }),
      new Promise((resolve) => {
        if (tempLocation) {
          locationAnalyzer(ANALYZER_ACTION.CLOSED, tempLocation.code, tempLocation.name);
        }

        resolve(true);
      }),
    ]);

    // delay close tab for waiting send data to tracking module
    const start = Date.now();
    let now = start;
    while (now - start < ANALYZER_TIME_DELAY) {
      if (promiseAll.every((i) => i === true)) now = Date.now();
    }

    // this is needed to avoid to show a confirmation prompt
    delete event['returnValue'];
  });

  // check condition active window and current event data
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.LOCATION.key) return;

    if (!isActivityWindow) {
      if (tempLocation) {
        locationAnalyzer(ANALYZER_ACTION.INTERRUPTED, tempLocation.code, tempLocation.name);
      } else if (tempSubGroup) {
        subGroupAnalyzer(ANALYZER_ACTION.INTERRUPTED, tempSubGroup.code, tempSubGroup.name);
      } else if (tempGroup) {
        groupAnalyzer(ANALYZER_ACTION.INTERRUPTED, tempGroup.code, tempGroup.name);
      }
    } else {
      if (tempLocation) {
        locationAnalyzer(ANALYZER_ACTION.RESUMED, tempLocation.code, tempLocation.name);
      } else if (tempSubGroup) {
        subGroupAnalyzer(ANALYZER_ACTION.RESUMED, tempSubGroup.code, tempSubGroup.name);
      } else if (tempGroup) {
        groupAnalyzer(ANALYZER_ACTION.RESUMED, tempGroup.code, tempGroup.name);
      }
    }
  }, [initial, isActivityWindow]);

  // check condition focus group
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.LOCATION.key) return;

    // check condition select new group
    if (group) {
      // selected new group and not store temp group
      if (!tempGroup) {
        groupAnalyzer(ANALYZER_ACTION.VIEWED, group.code, group.name);
      } else {
        groupAnalyzer(ANALYZER_ACTION.CLOSED, tempGroup.code, tempGroup.name);
        groupAnalyzer(ANALYZER_ACTION.VIEWED, group.code, group.name);
      }

      setTempGroup({ code: group.code, name: group.name, itemIds: group.itemIds });
    } else if (tempGroup) {
      groupAnalyzer(ANALYZER_ACTION.CLOSED, tempGroup.code, tempGroup.name);
      setTempGroup(undefined);
    }
  }, [initial, group]);

  // check condition focus sub-group
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.LOCATION.key || !tempGroup) return;

    // check condition select new or remove sub-group
    if (subGroup) {
      // selected new sub-group and not store temp sub-group
      if (!tempSubGroup) {
        subGroupAnalyzer(ANALYZER_ACTION.VIEWED, subGroup.code, subGroup.name);
      } else {
        // case select new parent group
        if (tempGroup.code !== group.code) {
          // close current sub-group in case temp group and current selected group not same
          subGroupAnalyzer(ANALYZER_ACTION.CLOSED, tempSubGroup.code, tempSubGroup.name);
          return;
        }

        subGroupAnalyzer(ANALYZER_ACTION.VIEWED, subGroup.code, subGroup.name);
      }

      setTempSubGroup({ code: subGroup.code, name: subGroup.name, itemIds: subGroup.itemIds });
    } else if (tempSubGroup) {
      // unselected sub-group
      subGroupAnalyzer(ANALYZER_ACTION.CLOSED, tempSubGroup.code, tempSubGroup.name);
      setTempSubGroup(undefined);
    }
  }, [initial, subGroup]);

  // check condition focus item
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.LOCATION.key || !tempGroup) return;

    const parentGroup = tempSubGroup || tempGroup;
    // check condition select new or remove item
    if (location && location.mapLocationInfo) {
      const locationInfo = location.mapLocationInfo;

      // selected new item and not store temp item
      if (!tempLocation) {
        locationAnalyzer(ANALYZER_ACTION.VIEWED, locationInfo.code, locationInfo.name);
      } else {
        if (tempLocation.code !== locationInfo.code) {
          locationAnalyzer(ANALYZER_ACTION.CLOSED, tempLocation.code, tempLocation.name);
        }

        if (parentGroup.itemIds.includes(location.id)) {
          locationAnalyzer(ANALYZER_ACTION.VIEWED, locationInfo.code, locationInfo.name);
        }
      }

      setTempLocation({ id: location.id, code: locationInfo.code, name: locationInfo.name });
    } else if (tempLocation) {
      // unselected item
      locationAnalyzer(ANALYZER_ACTION.CLOSED, tempLocation.code, tempLocation.name);
      setTempLocation(undefined);
    }
  }, [initial, group, tempGroup, location]);
};

export default useAnalyzerLocation;
